import { Localization } from "@viamap/viamap2-common";
import { DataCollector } from "src/dataCollector/DataCollector";
import { wfsEndpoints } from "src/dataCollector/WFSCollectorEndpoints";
import { Conflict, ConflictSearchParams } from "./ConflictSearchResults";
import { message } from "./Message";
import { featureCollection } from "@turf/helpers";
import union from "@turf/union";
import buffer from "@turf/buffer";


export async function searchSingleLayerForConflicts(geom: any, endpoint: keyof typeof wfsEndpoints, featureType: string, whoAmI?: string): Promise<any> {
   return await DataCollector.WFS.getFeatureIntersect(endpoint, featureType as keyof typeof wfsEndpoints[typeof endpoint]["featureTypes"], geom as any, {
      whoami: wfsEndpoints[endpoint]?.["whoAmI"] ? (whoAmI ?? undefined) : undefined
   })
}

export async function handleClickProfileSearch(searchParams: ConflictSearchParams, setConflicts: React.Dispatch<React.SetStateAction<Conflict[]>>) {
   let layers = searchParams.layers ?? []
   if (searchParams.pages) {
      layers = searchParams.pages.map(p => p.layers).flat();
   }
   let conflicts: Conflict[] = (layers).map((layer) => ({
      ...layer,
      conflict: null,
      intersect: null,
      progress: 0,
      err: "",
      matchedCount: -1
   }));
   setConflicts(conflicts);
   let geom = forceFeatureCollection(geomWithBuffer(searchParams.geom, searchParams.buffer));
   let perSource = Object.groupBy(conflicts, c => c.source)
   let resultingConflicts: Conflict[][] = await Promise.all(Object.keys(perSource)!.map(async (source) => {
      let sourceLayers = perSource[source];
      if (!sourceLayers) {
         message.error(Localization.getFormattedText("No layers found for source {source}", { source }));
         return [] as Conflict[];
      }
      for (let i = 0; i < sourceLayers.length; i++) {
         let layer = sourceLayers[i];
         let searchResult: WFSFeatureCollection | null = null;
         try {
            searchResult = await searchSingleLayerForConflicts(geom, layer.source as keyof typeof wfsEndpoints, layer.layer as string);
            if (!searchResult) {
               message.error(Localization.getFormattedText("No search result for layer {layer} in source {source}", { layer: layer.layer, source: layer.source }));
            }
            sourceLayers[i] = {
               ...sourceLayers[i],
               conflict: searchResult!,
               progress: 1,
               matchedCount: parseInt(searchResult?.metadata?.numberMatched || "0") || (searchResult?.features && searchResult.features.length) || 0
            };
         } catch (e) {
            sourceLayers[i] = {
               ...sourceLayers[i],
               conflict: null,
               progress: 1,
               err: e instanceof Error ? e.message : String(e),
               matchedCount: -1
            };
         }
         setConflicts((c) => {
            let newConflicts = c.map(conflict => {
               if (conflict.source === sourceLayers[i].source && conflict.layer === sourceLayers[i].layer) {
                  return sourceLayers[i];
               }
               return conflict;
            })
            return newConflicts;
         });
      }
      return sourceLayers;
   }));
   setConflicts(() => resultingConflicts.flat());
}

type WFSFeatureCollection = ReturnType<typeof featureCollection> & { metadata?: { endpoint?: string; numberMatched?: string; numberReturned?: number } };
export function geomWithBuffer(geom: any, bufferDistance?: number) {
   if (!geom) return featureCollection([]);
   if (bufferDistance) {
      if (geom.features.length > 1) return union(buffer(geom, bufferDistance, { units: 'meters' }) as any);
      return buffer(geom, bufferDistance, { units: 'meters' });
   }
   return geom;
}

export function forceFeatureCollection(geom: any) {
   if (geom.type === "FeatureCollection") return geom as WFSFeatureCollection;
   return featureCollection(geom.features ?? [geom]);
}